/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import {
  ChevronLeft, ChevronRight, Plus, X, Edit2, Trash2, Bell, Lock, BellOff, ChevronDown
} from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────
const C = {
  yellow: '#F7EC71', yellowLight: '#FEFCE8', yellowDark: '#E4AC49',
  orange: '#E4AC49', orangeLight: '#FDF3DC',
  green: '#7EA594', greenLight: '#EAF2EF',
  cream: '#F7F6EF', charcoal: '#2F3A42',
  navy: '#445366', mid: '#7A8A96', border: '#D8DEE3',
  skyBlue: '#BCD9E5', skyBlueLight: '#EAF4F8', skyText: '#3A8FA3',
  white: '#FFFFFF', red: '#DC3545', redLight: '#FFF0F0',
  pink: '#F2B8CB', pinkLight: '#FDF0F5', pinkText: '#C4607A',
  amber: '#E07B00', amberLight: '#FFF3E0',
  purple: '#7C3AED', purpleLight: '#F3EEFF',
};

const VENUES = ['Box Jordaan', 'Box Plantage', 'Sassys', 'Prep Kitchen'];
const ALL_ROLES = ['admin', 'manager', 'supervisor', 'bar', 'kitchen'];

const VENUE_COLORS = {
  'Box Jordaan':  '#1A8FA8',
  'Box Plantage': '#D4920A',
  'Sassys':       '#7C3AED',
  'Prep Kitchen': '#445366',
  'All Venues':   '#7EA594',
};
const VENUE_LIGHT = {
  'Box Jordaan':  '#D6EFF5',
  'Box Plantage': '#FAE8B8',
  'Sassys':       '#E9DAFF',
  'Prep Kitchen': '#D6DCE4',
  'All Venues':   '#EAF2EF',
};

const SPECIAL_TYPES = {
  public_holiday: { color: '#6B8494', light: '#DDE6EC', label: 'Public Holiday' },
  contract_end:   { color: '#C0212F', light: '#FAD0D3', label: 'Contract End'   },
  probation_end:  { color: '#7A4828', light: '#EDD8C8', label: 'Probation End'  },
  birthday:       { color: '#B84468', light: '#F5C6D6', label: 'Birthday'       },
};

const EVENT_TYPES = [
  { value: 'general',     label: 'General'     },
  { value: 'important',   label: 'Important'   },
  { value: 'meeting',     label: 'Meeting'     },
  { value: 'staff_event', label: 'Staff Event' },
];

const resolveColor = (type, venue) =>
  SPECIAL_TYPES[type]?.color || VENUE_COLORS[venue] || VENUE_COLORS['All Venues'];
const resolveLight = (type, venue) =>
  SPECIAL_TYPES[type]?.light || VENUE_LIGHT[venue] || VENUE_LIGHT['All Venues'];

// ─── Dutch Public Holidays ────────────────────────────────────────────────────
function getNLHolidays(year) {
  const holidays = [
    { title: 'Nieuwjaarsdag',     date: `${year}-01-01` },
    { title: 'Bevrijdingsdag',    date: `${year}-05-05` },
    { title: 'Prinsjesdag',       date: `${year}-09-17` },
    { title: 'Sinterklaas',       date: `${year}-12-05` },
    { title: 'Eerste Kerstdag',   date: `${year}-12-25` },
    { title: 'Tweede Kerstdag',   date: `${year}-12-26` },
    { title: 'Koningsdag',        date: `${year}-04-27` },
  ];
  const easter = getEasterDate(year);
  const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
  const fmt = (d) => d.toISOString().split('T')[0];
  holidays.push({ title: 'Eerste Paasdag',     date: fmt(easter)            });
  holidays.push({ title: 'Tweede Paasdag',     date: fmt(addDays(easter, 1))});
  holidays.push({ title: 'Goede Vrijdag',      date: fmt(addDays(easter, -2))});
  holidays.push({ title: 'Hemelvaartsdag',     date: fmt(addDays(easter, 39))});
  holidays.push({ title: 'Eerste Pinksterdag', date: fmt(addDays(easter, 49))});
  holidays.push({ title: 'Tweede Pinksterdag', date: fmt(addDays(easter, 50))});
  return holidays.map(h => ({ ...h, type: 'public_holiday', color: SPECIAL_TYPES.public_holiday.color, isHoliday: true }));
}

function getEasterDate(year) {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

// ─── Calendar helpers ─────────────────────────────────────────────────────────
function getDaysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function getFirstDayOfMonth(year, month) {
  let day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];
const DAY_NAMES   = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

function expandRecurring(events, year, month) {
  const result = [];
  const monthStart = new Date(year, month, 1);
  const monthEnd   = new Date(year, month + 1, 0);
  events.forEach(ev => {
    const base = new Date(ev.date);
    if (!ev.recurrence || ev.recurrence === 'none') {
      if (base >= monthStart && base <= monthEnd) result.push({ ...ev });
      return;
    }
    let cursor = new Date(base);
    while (cursor < monthStart) cursor = advanceByRecurrence(cursor, ev.recurrence);
    let safety = 0;
    while (cursor <= monthEnd && safety < 60) {
      result.push({ ...ev, date: cursor.toISOString().split('T')[0] });
      cursor = advanceByRecurrence(cursor, ev.recurrence);
      safety++;
    }
  });
  return result;
}

function advanceByRecurrence(date, recurrence) {
  const d = new Date(date);
  if (recurrence === 'weekly')  d.setDate(d.getDate() + 7);
  if (recurrence === 'monthly') d.setMonth(d.getMonth() + 1);
  if (recurrence === 'yearly')  d.setFullYear(d.getFullYear() + 1);
  return d;
}

// ─── Notification helpers ─────────────────────────────────────────────────────
function getActiveNotifications(notifications, events, userRole, userVenue, today) {
  const todayDate = new Date(today);
  todayDate.setHours(0, 0, 0, 0);
  return notifications.filter(n => {
    if (!n.notify_roles?.includes(userRole) && !n.notify_roles?.includes('all')) return false;
    if (n.notify_venues?.length && !n.notify_venues.includes('All Venues') && !n.notify_venues.includes(userVenue)) return false;
    const ev = events.find(e => e.id === n.event_id);
    if (!ev) return false;
    const eventDate = new Date(ev.date);
    eventDate.setHours(0, 0, 0, 0);
    const alertFrom = new Date(eventDate);
    alertFrom.setDate(alertFrom.getDate() - (n.lead_days || 1));
    return todayDate >= alertFrom && todayDate <= eventDate;
  }).map(n => ({ ...n, event: events.find(e => e.id === n.event_id) }));
}

function isDismissed(notifId, userId) {
  try { return localStorage.getItem(`notif_dismissed_${notifId}_${userId}`) === '1'; } catch { return false; }
}
function setDismissed(notifId, userId) {
  try { localStorage.setItem(`notif_dismissed_${notifId}_${userId}`, '1'); } catch {}
}

// ─── Shared form styles ───────────────────────────────────────────────────────
const labelStyle = {
  fontSize: '11px', fontWeight: 700, color: C.mid,
  display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.06em',
};
const inputStyle = {
  width: '100%', padding: '9px 11px', border: `1.5px solid ${C.border}`,
  borderRadius: '8px', fontSize: '13px', color: C.charcoal,
  background: C.white, outline: 'none', boxSizing: 'border-box',
  fontFamily: 'inherit', transition: 'border-color 0.15s',
};
const pill = (active, small = false) => ({
  padding: small ? '0.28rem 0.85rem' : '0.35rem 1rem',
  borderRadius: '999px',
  border: `1.5px solid ${active ? C.navy : C.border}`,
  background: active ? C.navy : C.white,
  color: active ? C.white : C.mid,
  fontSize: small ? '0.78rem' : '0.83rem',
  fontWeight: active ? 700 : 500,
  cursor: 'pointer',
  transition: 'all 0.15s',
  whiteSpace: 'nowrap',
});

// ─── Multi-select toggle ──────────────────────────────────────────────────────
function MultiToggle({ label, options, selected, onChange }) {
  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };
  const allSelected = options.every(o => selected.includes(o));
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <label style={labelStyle}>{label}</label>
        <button onClick={() => onChange(allSelected ? [] : options)}
          style={{ fontSize: '11px', color: C.skyText, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
          {allSelected ? 'None' : 'All'}
        </button>
      </div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {options.map(opt => {
          const active = selected.includes(opt);
          return (
            <button key={opt} onClick={() => toggle(opt)} style={{
              padding: '4px 10px', borderRadius: '12px',
              border: `1.5px solid ${active ? C.green : C.border}`,
              background: active ? C.greenLight : C.cream,
              color: active ? C.green : C.mid,
              fontSize: '11px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.12s',
            }}>{opt}</button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Notification Banner ──────────────────────────────────────────────────────
export function NotificationBanner({ notifications, events, userRole, userVenue, userId, compact = false }) {
  const today = new Date().toISOString().split('T')[0];
  const [dismissed, setDismissedState] = useState({});
  const active = getActiveNotifications(notifications, events, userRole, userVenue, today)
    .filter(n => !dismissed[n.id] && !isDismissed(n.id, userId));
  if (!active.length) return null;
  const dismiss = (id) => { setDismissed(id, userId); setDismissedState(prev => ({ ...prev, [id]: true })); };

  if (compact) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: C.amberLight, border: `1px solid ${C.amber}44`, borderRadius: '20px', padding: '4px 10px 4px 8px' }}>
        <Bell size={13} color={C.amber} />
        <span style={{ fontSize: '12px', fontWeight: 700, color: C.amber }}>{active.length}</span>
        <span style={{ fontSize: '12px', color: C.amber }}>{active.length === 1 ? 'upcoming alert' : 'upcoming alerts'}</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
      {active.map(n => (
        <div key={n.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', background: C.amberLight, border: `1px solid ${C.amber}55`, borderRadius: '10px', padding: '12px 14px' }}>
          <Bell size={15} color={C.amber} style={{ marginTop: '1px', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: C.charcoal }}>{n.event?.title}</div>
            <div style={{ fontSize: '11px', color: C.mid, marginTop: '2px' }}>{n.event?.date} · {n.event?.venue} · Reminder {n.lead_days === 0 ? 'today' : `${n.lead_days}d before`}</div>
          </div>
          <button onClick={() => dismiss(n.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid, padding: '0', flexShrink: 0 }}><X size={14} /></button>
        </div>
      ))}
    </div>
  );
}

// ─── Venue dropdown — minimal, app-consistent ─────────────────────────────────
function VenueDropdown({ selectedVenue, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const allOptions = ['All Venues', ...VENUES];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const activeColor = selectedVenue === 'All Venues' ? C.navy : VENUE_COLORS[selectedVenue];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '6px 12px', borderRadius: '20px',
          border: `1.5px solid ${open ? activeColor : C.border}`,
          background: C.white, cursor: 'pointer',
          fontSize: '0.82rem', fontWeight: 600,
          color: C.charcoal, transition: 'all 0.15s',
          boxShadow: open ? `0 0 0 3px ${activeColor}18` : 'none',
        }}
      >
        {/* Venue colour dot */}
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: activeColor, flexShrink: 0 }} />
        {selectedVenue}
        <ChevronDown size={13} color={C.mid} style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          background: C.white, borderRadius: '12px',
          border: `1px solid ${C.border}`,
          boxShadow: '0 8px 24px rgba(68,83,102,0.13)',
          zIndex: 300, minWidth: '170px', overflow: 'hidden',
          padding: '4px',
        }}>
          {allOptions.map(v => {
            const isActive = selectedVenue === v;
            const vColor = v === 'All Venues' ? C.navy : VENUE_COLORS[v];
            return (
              <button
                key={v}
                onClick={() => { onChange(v); setOpen(false); }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '9px',
                  padding: '8px 10px', borderRadius: '8px', border: 'none',
                  background: isActive ? `${vColor}12` : 'transparent',
                  cursor: 'pointer', textAlign: 'left',
                  fontSize: '13px', fontWeight: isActive ? 700 : 500,
                  color: isActive ? vColor : C.charcoal,
                  transition: 'background 0.1s',
                }}
              >
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: vColor, flexShrink: 0 }} />
                {v}
                {isActive && <div style={{ marginLeft: 'auto', width: '6px', height: '6px', borderRadius: '50%', background: vColor }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Event list item ──────────────────────────────────────────────────────────
function EventListItem({ ev, canEdit, isAdmin, onEdit, onDelete }) {
  const isRestricted = (ev.visibleToUsers && ev.visibleToUsers.length > 0) ||
    (ev.visibleToRoles && !ev.visibleToRoles.includes('bar') && !ev.visibleToRoles.includes('kitchen'));
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '12px',
      padding: '12px 0',
    }}>
      {/* Colour dot — matches reference */}
      <div style={{ marginTop: '4px', width: '8px', height: '8px', borderRadius: '50%', background: ev.color, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ fontSize: '14px', fontWeight: 700, color: C.charcoal }}>{ev.title}</span>
          {isRestricted && isAdmin && <Lock size={10} color={C.mid} />}
        </div>
        <div style={{ fontSize: '12px', color: C.mid, marginTop: '2px' }}>
          {ev.venue === 'All Venues' ? 'All venues' : ev.venue}
          {ev.recurrence && ev.recurrence !== 'none' ? ` · ${ev.recurrence}` : ''}
          {ev.isHoliday ? ' · Public Holiday' : ''}
          {ev.isTeam ? ' · Team' : ''}
        </div>
        {ev.notes && (
          <div style={{ fontSize: '12px', color: C.charcoal, marginTop: '5px', padding: '5px 8px', background: C.cream, borderRadius: '6px', lineHeight: 1.4 }}>
            {ev.notes}
          </div>
        )}
      </div>
      {canEdit && !ev.isTeam && !ev.isHoliday && (
        <div style={{ display: 'flex', gap: '2px', flexShrink: 0, marginTop: '2px' }}>
          <button onClick={() => onEdit(ev)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid, padding: '4px', borderRadius: '5px' }}>
            <Edit2 size={13} />
          </button>
          <button onClick={() => onDelete(ev)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.red, padding: '4px', borderRadius: '5px' }}>
            <Trash2 size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main Calendar Page ───────────────────────────────────────────────────────
export default function CalendarPage({ user, userRole, userVenue }) {
  const today    = new Date();
  const todayStr = today.toISOString().split('T')[0];

  const [currentYear,   setCurrentYear]   = useState(today.getFullYear());
  const [currentMonth,  setCurrentMonth]  = useState(today.getMonth());
  const [selectedVenue, setSelectedVenue] = useState('All Venues');
  const [selectedDay,   setSelectedDay]   = useState(null); // null = no popup; set on day click
  const [events,        setEvents]        = useState([]);
  const [teamEvents,    setTeamEvents]    = useState([]);
  const [holidays,      setHolidays]      = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showModal,     setShowModal]     = useState(false);
  const [editingEvent,  setEditingEvent]  = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState('');
  const [dismissedBadge, setDismissedBadge] = useState(false);
  const [adminUsers,    setAdminUsers]    = useState([]);

  // Responsive breakpoint — full reactive so tablet resize works
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;

  const isAdmin  = userRole === 'admin';
  const canEdit  = ['admin', 'manager'].includes(userRole);

  const defaultForm = () => ({
    title: '', venue: userVenue || 'Box Jordaan', date: '',
    end_date: '', recurrence: 'none', type: 'general', notes: '',
    visible_to_roles: [...ALL_ROLES],
    visible_to_venues: [...VENUES, 'All Venues'],
    visible_to_users: [],
    restrict_to_users: false,
    notify_enabled: false,
    notify_roles: [], notify_venues: [], lead_days: 1,
  });
  const [form, setForm] = useState(defaultForm());

  // ─── Data loading ─────────────────────────────────────────────────────────
  const loadEvents = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.from('calendar_events').select('*');
    if (!error && data) {
      setEvents(data.map(e => ({
        id: e.id, title: e.title, venue: e.venue,
        date: e.date, endDate: e.end_date,
        recurrence: e.recurrence || 'none',
        type: e.type, color: resolveColor(e.type, e.venue),
        notes: e.notes || '',
        createdBy: e.created_by,
        visibleToRoles: e.visible_to_roles || ALL_ROLES,
        visibleToVenues: e.visible_to_venues || [...VENUES, 'All Venues'],
        visibleToUsers: e.visible_to_users || [],
      })));
    }
    setLoading(false);
  }, []);

  const loadNotifications = useCallback(async () => {
    const { data } = await supabase.from('calendar_notifications').select('*');
    if (data) setNotifications(data);
  }, []);

  const loadTeamEvents = useCallback(async () => {
    const { data } = await supabase.from('team_members').select('id, name, venue, contract_end_date, birthday').eq('active', true);
    if (data) {
      const evs = [];
      data.forEach(tm => {
        if (tm.contract_end_date) evs.push({
          id: `tm-contract-${tm.id}`, title: `${tm.name} – Contract End`,
          venue: tm.venue, date: tm.contract_end_date,
          type: 'contract_end', color: resolveColor('contract_end', tm.venue),
          isTeam: true, visibleToRoles: ['admin', 'manager'],
        });
        if (tm.birthday) {
          const [, mm, dd] = tm.birthday.split('-');
          const thisYearDate = `${currentYear}-${mm}-${dd}`;
          const todayDs = new Date().toISOString().split('T')[0];
          evs.push({ id: `tm-bday-${tm.id}`, title: `🎂 ${tm.name}`, venue: tm.venue, date: thisYearDate, type: 'birthday', color: resolveColor('birthday', tm.venue), isTeam: true, visibleToRoles: ALL_ROLES });
          if (thisYearDate < todayDs) {
            evs.push({ id: `tm-bday-next-${tm.id}`, title: `🎂 ${tm.name}`, venue: tm.venue, date: `${currentYear + 1}-${mm}-${dd}`, type: 'birthday', color: resolveColor('birthday', tm.venue), isTeam: true, visibleToRoles: ALL_ROLES });
          }
        }
      });
      setTeamEvents(evs);
    }
  }, [currentYear]);

  useEffect(() => {
    loadEvents(); loadTeamEvents(); loadNotifications();
    setHolidays(getNLHolidays(currentYear));
    supabase.from('profiles').select('id, name, role')
      .in('role', ['admin', 'manager']).order('name')
      .then(({ data }) => { if (data) setAdminUsers(data); });
  }, [loadEvents, loadTeamEvents, loadNotifications, currentYear]);

  const isVisible = (ev) => {
    if (isAdmin) return true;
    if (ev.visibleToUsers && ev.visibleToUsers.length > 0) return ev.visibleToUsers.includes(user?.id);
    return (ev.visibleToRoles || ALL_ROLES).includes(userRole);
  };

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentYear(y => y - 1); setCurrentMonth(11); }
    else setCurrentMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentYear(y => y + 1); setCurrentMonth(0); }
    else setCurrentMonth(m => m + 1);
  };

  const allEventsForMonth = () => {
    const venueFilter = (e) => selectedVenue === 'All Venues' || e.venue === selectedVenue || e.venue === 'All Venues';
    const expanded = expandRecurring(events, currentYear, currentMonth).filter(venueFilter).filter(isVisible);
    const filteredTeam = teamEvents.filter(e => {
      if (!venueFilter(e) || !isVisible(e)) return false;
      const d = new Date(e.date);
      return d.getFullYear() === currentYear && d.getMonth() === currentMonth;
    });
    const filteredHolidays = holidays.filter(h => {
      const d = new Date(h.date);
      return d.getFullYear() === currentYear && d.getMonth() === currentMonth;
    });
    return [...expanded, ...filteredTeam, ...filteredHolidays];
  };

  const getEventsForDay = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return allEventsForMonth().filter(e => {
      if (e.endDate && e.endDate > e.date) return dateStr >= e.date && dateStr <= e.endDate;
      return e.date === dateStr;
    });
  };

  // ─── Upcoming events (next 30 days, excluding today) ──────────────────────
  const getUpcomingEvents = () => {
    const now = new Date();
    const venueFilter = (e) => selectedVenue === 'All Venues' || e.venue === selectedVenue || e.venue === 'All Venues';
    const allPool = [
      ...expandRecurring(events, currentYear, currentMonth),
      ...expandRecurring(events, currentYear, currentMonth + 1),
      ...teamEvents,
      ...holidays,
    ].filter(venueFilter).filter(isVisible);

    const result = [];
    for (let i = 1; i <= 30; i++) {
      const d = new Date(now);
      d.setDate(d.getDate() + i);
      const ds = d.toISOString().split('T')[0];
      const dayEvs = allPool.filter(e => {
        if (e.endDate && e.endDate > e.date) return ds >= e.date && ds <= e.endDate;
        if (e.recurrence && e.recurrence !== 'none') {
          const base = new Date(e.date);
          const target = new Date(ds);
          if (target < base) return false;
          const diffDays = Math.round((target - base) / 86400000);
          if (e.recurrence === 'weekly')  return diffDays % 7 === 0;
          if (e.recurrence === 'monthly') return base.getDate() === target.getDate();
          if (e.recurrence === 'yearly')  return base.getMonth() === target.getMonth() && base.getDate() === target.getDate();
        }
        return e.date === ds;
      });
      if (dayEvs.length > 0) result.push({ ds, d, events: dayEvs });
    }
    return result;
  };

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const openCreate = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setEditingEvent(null);
    setForm({ ...defaultForm(), date: dateStr });
    setShowModal(true);
  };

  const openEdit = (ev) => {
    setEditingEvent(ev);
    setForm({
      title: ev.title, venue: ev.venue, date: ev.date,
      end_date: ev.endDate || '', recurrence: ev.recurrence || 'none',
      type: ev.type, notes: ev.notes || '',
      visible_to_roles: ev.visibleToRoles || [...ALL_ROLES],
      visible_to_venues: ev.visibleToVenues || [...VENUES, 'All Venues'],
      visible_to_users: ev.visibleToUsers || [],
      restrict_to_users: (ev.visibleToUsers && ev.visibleToUsers.length > 0),
      notify_enabled: false, notify_roles: [], notify_venues: [], lead_days: 1,
    });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingEvent(null); setError(''); };
  const handleFormChange = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const saveEvent = async () => {
    if (!form.title.trim() || !form.date) { setError('Title and date are required.'); return; }
    setSaving(true); setError('');
    const payload = {
      title: form.title.trim(), venue: form.venue, date: form.date,
      end_date: form.end_date || null, recurrence: form.recurrence,
      type: form.type, notes: form.notes?.trim() || null,
      colour: resolveColor(form.type, form.venue), created_by: user?.id,
      visible_to_roles: form.visible_to_roles,
      visible_to_venues: form.visible_to_venues,
      visible_to_users: form.restrict_to_users ? form.visible_to_users : [],
    };
    let err, eventId;
    if (editingEvent) {
      ({ error: err } = await supabase.from('calendar_events').update(payload).eq('id', editingEvent.id));
      eventId = editingEvent.id;
    } else {
      const { data, error: insertErr } = await supabase.from('calendar_events').insert([payload]).select('id').single();
      err = insertErr; eventId = data?.id;
    }
    if (err) { setSaving(false); setError(err.message); return; }
    if (form.notify_enabled && eventId && form.notify_roles.length > 0) {
      await supabase.from('calendar_notifications').insert([{
        event_id: eventId,
        notify_roles: form.notify_roles,
        notify_venues: form.notify_venues.length ? form.notify_venues : [...VENUES, 'All Venues'],
        lead_days: form.lead_days,
      }]);
    }
    setSaving(false); closeModal(); loadEvents(); loadNotifications();
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    await supabase.from('calendar_events').delete().eq('id', ev.id);
    await supabase.from('calendar_notifications').delete().eq('event_id', ev.id);
    loadEvents(); loadNotifications();
  };

  const activeNotifs = getActiveNotifications(notifications, events, userRole, userVenue, todayStr)
    .filter(n => !isDismissed(n.id, user?.id));

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay    = getFirstDayOfMonth(currentYear, currentMonth);
  const totalCells  = Math.ceil((daysInMonth + firstDay) / 7) * 7;

  const navBtnStyle = {
    background: C.white, border: `1.5px solid ${C.border}`,
    borderRadius: '8px', padding: '6px 10px',
    cursor: 'pointer', display: 'flex', alignItems: 'center',
    transition: 'all 0.15s',
  };

  // ─── Mobile / tablet calendar grid ────────────────────────────────────────
  // Compact grid: just numbers + dot indicator, no event labels crammed in
  const renderCompactGrid = () => {
    const monthEvs = allEventsForMonth();
    return (
      <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: C.skyBlueLight, borderBottom: `1px solid ${C.skyBlue}` }}>
          {DAY_NAMES.map((d, i) => (
            <div key={d} style={{
              padding: '10px 0', textAlign: 'center',
              fontSize: '0.65rem', fontWeight: 700,
              color: i >= 5 ? C.amber : C.skyText,
              letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>{d}</div>
          ))}
        </div>

        {/* Cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '6px 4px 10px' }}>
          {Array.from({ length: totalCells }).map((_, idx) => {
            const dayNum = idx - firstDay + 1;
            const isCurrentMonth = dayNum >= 1 && dayNum <= daysInMonth;
            const dateStr = isCurrentMonth
              ? `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`
              : null;
            const isToday     = dateStr === todayStr;
            const isSelected  = isCurrentMonth && dayNum === selectedDay && (currentYear !== today.getFullYear() || currentMonth !== today.getMonth() || dayNum !== today.getDate() || !isToday);
            const isHighlight = isToday || (isCurrentMonth && dayNum === selectedDay);
            const isWeekend   = idx % 7 >= 5;
            const dayEvs      = isCurrentMonth ? monthEvs.filter(e => {
              if (e.endDate && e.endDate > e.date) return dateStr >= e.date && dateStr <= e.endDate;
              return e.date === dateStr;
            }) : [];
            const hasDot = dayEvs.length > 0;
            const dotColors = dayEvs.slice(0, 3).map(e => e.color);

            return (
              <div
                key={idx}
                onClick={() => { if (isCurrentMonth) setSelectedDay(dayNum); }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  gap: '3px', padding: '4px 2px',
                  cursor: isCurrentMonth ? 'pointer' : 'default',
                }}
              >
                <div style={{
                  width: isTablet ? '36px' : '32px',
                  height: isTablet ? '36px' : '32px',
                  borderRadius: '50%',
                  background: isHighlight
                    ? (isToday ? C.navy : C.skyBlue)
                    : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s',
                }}>
                  <span style={{
                    fontSize: isTablet ? '14px' : '13px',
                    fontWeight: isHighlight ? 800 : 400,
                    color: isHighlight
                      ? (isToday ? C.white : C.navy)
                      : isCurrentMonth
                        ? (isWeekend ? C.amber : C.charcoal)
                        : '#C8D4DC',
                    lineHeight: 1,
                  }}>
                    {isCurrentMonth ? dayNum : ''}
                  </span>
                </div>
                {/* Dot indicators */}
                <div style={{ display: 'flex', gap: '2px', height: '5px', alignItems: 'center' }}>
                  {hasDot && dotColors.slice(0, 3).map((col, i) => (
                    <div key={i} style={{ width: '4px', height: '4px', borderRadius: '50%', background: isHighlight ? C.skyText : col }} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── Selected day event list (mobile/tablet) ───────────────────────────────
  const renderSelectedDayEvents = () => {
    const selectedDateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
    const isSelectedToday = selectedDateStr === todayStr;
    const dayEvs = getEventsForDay(selectedDay);
    const dayDate = new Date(currentYear, currentMonth, selectedDay);
    const dayName = DAY_NAMES[dayDate.getDay() === 0 ? 6 : dayDate.getDay() - 1];

    return (
      <div style={{ marginTop: '20px' }}>
        {/* Section header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
          <div>
            <span style={{ fontSize: '13px', fontWeight: 800, color: C.charcoal }}>
              {isSelectedToday ? 'Today' : `${dayName}, ${selectedDay} ${MONTH_NAMES[currentMonth]}`}
            </span>
          </div>
          {canEdit && (
            <button
              onClick={() => openCreate(selectedDay)}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 12px', background: C.navy, color: C.white, border: 'none', borderRadius: '20px', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}
            >
              <Plus size={12} /> Add
            </button>
          )}
        </div>

        {dayEvs.length === 0 ? (
          <div style={{ padding: '20px 0 8px', color: C.mid, fontSize: '13px' }}>No events</div>
        ) : (
          <div style={{ borderTop: `1px solid ${C.border}` }}>
            {dayEvs.map((ev, i) => (
              <div key={ev.id + i} style={{ borderBottom: i < dayEvs.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                <EventListItem ev={ev} canEdit={canEdit} isAdmin={isAdmin} onEdit={openEdit} onDelete={deleteEvent} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ─── Upcoming section (mobile/tablet) ─────────────────────────────────────
  const renderUpcoming = () => {
    const upcoming = getUpcomingEvents();
    if (upcoming.length === 0) return null;

    return (
      <div style={{ marginTop: '28px' }}>
        <div style={{ fontSize: '13px', fontWeight: 800, color: C.charcoal, marginBottom: '4px' }}>Upcoming</div>
        <div style={{ borderTop: `1px solid ${C.border}` }}>
          {upcoming.map(({ ds, d, events: dayEvs }) => {
            const dayName = DAY_NAMES[d.getDay() === 0 ? 6 : d.getDay() - 1];
            const dayNum  = d.getDate();
            const mon     = MONTH_NAMES[d.getMonth()].slice(0, 3);
            const isTom   = (() => { const t = new Date(today); t.setDate(t.getDate() + 1); return ds === t.toISOString().split('T')[0]; })();
            return (
              <div key={ds}>
                {/* Date label */}
                <div style={{ padding: '10px 0 2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {isTom ? 'Tomorrow' : `${dayName} ${dayNum} ${mon}`}
                  </span>
                </div>
                {dayEvs.map((ev, i) => (
                  <div key={ev.id + i} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <EventListItem ev={ev} canEdit={canEdit} isAdmin={isAdmin} onEdit={openEdit} onDelete={deleteEvent} />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── Desktop grid (unchanged) ──────────────────────────────────────────────
  const renderDesktopGrid = () => (
    <>
      <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.skyBlue}`, overflow: 'hidden', boxShadow: '0 2px 16px rgba(188,217,229,0.25)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: C.skyBlueLight, borderBottom: `2px solid ${C.skyBlue}` }}>
          {DAY_NAMES.map((d, i) => (
            <div key={d} style={{
              padding: '0.6rem 0', textAlign: 'center',
              fontSize: '0.68rem', fontWeight: 700,
              color: i >= 5 ? C.amber : C.skyText,
              letterSpacing: '0.08em', textTransform: 'uppercase',
            }}>{d}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {Array.from({ length: totalCells }).map((_, idx) => {
            const dayNum = idx - firstDay + 1;
            const isCurrentMonth = dayNum >= 1 && dayNum <= daysInMonth;
            const dateStr = isCurrentMonth
              ? `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`
              : null;
            const isToday   = dateStr === todayStr;
            const dayEvs    = isCurrentMonth ? getEventsForDay(dayNum) : [];
            const isWeekend = idx % 7 >= 5;
            const hasHoliday = dayEvs.some(e => e.isHoliday);
            const cellBg = !isCurrentMonth ? '#E8F4F8' : isToday ? C.yellowLight : C.white;

            return (
              <div
                key={idx}
                onClick={() => { if (isCurrentMonth) { setSelectedDay(dayNum); } }}
                style={{
                  minHeight: '96px', padding: '6px 6px 4px',
                  borderBottom: `1px solid ${C.skyBlue}88`,
                  borderRight: (idx + 1) % 7 === 0 ? 'none' : `1px solid ${C.skyBlue}88`,
                  background: cellBg, cursor: isCurrentMonth ? 'pointer' : 'default',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => { if (isCurrentMonth && !isToday) e.currentTarget.style.background = C.skyBlueLight; }}
                onMouseLeave={e => { e.currentTarget.style.background = cellBg; }}
              >
                {isCurrentMonth && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                      <div style={{
                        width: '24px', height: '24px', borderRadius: '50%',
                        background: isToday ? C.navy : 'transparent',
                        color: isToday ? C.white : isWeekend ? C.amber : C.charcoal,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '12px', fontWeight: isToday ? 800 : 500, flexShrink: 0,
                      }}>{dayNum}</div>
                      {hasHoliday && <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: SPECIAL_TYPES.public_holiday.color }} />}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {dayEvs.filter(e => !e.isHoliday).slice(0, 3).map((ev, i) => {
                        const isRestricted = ev.visibleToRoles && !ev.visibleToRoles.includes('bar') && !ev.visibleToRoles.includes('kitchen');
                        const bgLight = resolveLight(ev.type, ev.venue);
                        return (
                          <div key={ev.id + i} style={{
                            fontSize: '10px', padding: '2px 5px 2px 6px', borderRadius: '4px',
                            fontWeight: 700, background: bgLight, color: C.charcoal,
                            borderLeft: `3px solid ${ev.color}`,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            display: 'flex', alignItems: 'center', gap: '3px',
                          }}>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.title}</span>
                            {isRestricted && isAdmin && <Lock size={8} style={{ flexShrink: 0, opacity: 0.5 }} />}
                          </div>
                        );
                      })}
                      {dayEvs.filter(e => e.isHoliday).map((ev, i) => (
                        <div key={ev.id + i} style={{
                          fontSize: '9px', padding: '2px 5px', borderRadius: '3px', fontWeight: 600,
                          background: SPECIAL_TYPES.public_holiday.light, color: C.charcoal,
                          borderLeft: `3px solid ${SPECIAL_TYPES.public_holiday.color}`,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{ev.title}</div>
                      ))}
                      {dayEvs.length > 3 && (
                        <div style={{ fontSize: '10px', color: C.mid, paddingLeft: '4px', fontWeight: 600 }}>+{dayEvs.length - 3} more</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop legend */}
      <div style={{ marginTop: '1rem', display: 'flex', gap: '0', flexWrap: 'wrap', alignItems: 'center', rowGap: '8px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          {[...VENUES.map(v => ({ label: v, color: VENUE_COLORS[v] })), { label: 'All Venues', color: VENUE_COLORS['All Venues'] }].map(item => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: C.mid }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: item.color }} />{item.label}
            </div>
          ))}
        </div>
        <div style={{ width: '1px', height: '16px', background: C.border, margin: '0 14px' }} />
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          {Object.entries(SPECIAL_TYPES).map(([key, val]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: C.mid }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: val.color }} />{val.label}
            </div>
          ))}
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: C.mid, borderLeft: `1px solid ${C.border}`, paddingLeft: '14px', marginLeft: '2px' }}>
            <Lock size={9} color={C.mid} /> Restricted
          </div>
        )}
      </div>

      {/* Desktop day popup — fires on cell click */}
      {selectedDay && (
        <DesktopDayPopup
          day={selectedDay}
          dateStr={`${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`}
          events={getEventsForDay(selectedDay)}
          canEdit={canEdit} isAdmin={isAdmin}
          onEdit={openEdit} onDelete={deleteEvent}
          onAdd={() => openCreate(selectedDay)}
          onClose={() => setSelectedDay(null)}
          currentMonth={currentMonth} currentYear={currentYear}
        />
      )}
    </>
  );

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: isMobile ? '1rem' : '1.5rem', maxWidth: '1400px', margin: '0 auto', fontFamily: 'system-ui, sans-serif' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isMobile ? '1rem' : '1.25rem', gap: '0.75rem' }}>
        <div>
          <h2 style={{ margin: '0 0 0.2rem', fontSize: isMobile ? '1.3rem' : '1.5rem', fontWeight: 800, color: C.navy, letterSpacing: '-0.02em' }}>Calendar</h2>
          <p style={{ margin: 0, fontSize: '0.82rem', color: C.mid }}>Events, team dates &amp; public holidays</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {activeNotifs.length > 0 && !dismissedBadge && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: C.amberLight, border: `1px solid ${C.amber}55`, borderRadius: '20px', padding: '5px 10px 5px 8px' }}>
              <Bell size={13} color={C.amber} />
              <span style={{ fontSize: '12px', fontWeight: 700, color: C.amber }}>{activeNotifs.length}</span>
              {!isMobile && <span style={{ fontSize: '12px', color: C.amber }}>{activeNotifs.length === 1 ? 'alert' : 'alerts'}</span>}
              <button onClick={() => setDismissedBadge(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.amber, padding: '0 0 0 2px', display: 'flex', alignItems: 'center' }}>
                <X size={12} />
              </button>
            </div>
          )}
          {canEdit && !isMobile && (
            <button onClick={() => openCreate(today.getDate())} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '0.5rem 1.25rem', background: C.navy, color: C.white, border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700 }}>
              <Plus size={14} /> New Event
            </button>
          )}
        </div>
      </div>

      {/* Month nav + venue dropdown */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isMobile ? '0.85rem' : '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button onClick={prevMonth} style={navBtnStyle}
            onMouseEnter={e => e.currentTarget.style.borderColor = C.navy}
            onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
            <ChevronLeft size={16} color={C.charcoal} />
          </button>
          <h3 style={{ margin: 0, fontSize: isMobile ? '1rem' : '1.1rem', fontWeight: 800, color: C.charcoal, minWidth: isMobile ? '140px' : '190px', textAlign: 'center', letterSpacing: '-0.02em' }}>
            {MONTH_NAMES[currentMonth]} <span style={{ color: C.mid, fontWeight: 400 }}>{currentYear}</span>
          </h3>
          <button onClick={nextMonth} style={navBtnStyle}
            onMouseEnter={e => e.currentTarget.style.borderColor = C.navy}
            onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
            <ChevronRight size={16} color={C.charcoal} />
          </button>
          {!isMobile && (
            <button onClick={() => { setCurrentYear(today.getFullYear()); setCurrentMonth(today.getMonth()); setSelectedDay(today.getDate()); }}
              style={{ ...pill(false, true), marginLeft: '2px' }}>
              Today
            </button>
          )}
        </div>

        {/* Venue dropdown — right aligned, minimal */}
        <VenueDropdown selectedVenue={selectedVenue} onChange={setSelectedVenue} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: C.mid }}>Loading…</div>
      ) : isMobile || isTablet ? (
        <>
          {renderCompactGrid()}
          {selectedDay !== null && renderSelectedDayEvents()}
          {renderUpcoming()}
        </>
      ) : (
        renderDesktopGrid()
      )}

      {/* Create/Edit modal */}
      {showModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(47,58,66,0.4)', zIndex: 1100, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? '0' : '20px' }}
          onClick={closeModal}
        >
          <div
            style={{ background: C.white, borderRadius: isMobile ? '20px 20px 0 0' : '16px', padding: isMobile ? '20px 16px 32px' : '28px', width: isMobile ? '100%' : '520px', maxWidth: isMobile ? '100%' : '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 16px 50px rgba(0,0,0,0.22)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '22px' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: C.navy }}>
                {editingEvent ? '✏️ Edit Event' : '✨ New Event'}
              </h3>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid }}><X size={20} /></button>
            </div>

            {error && (
              <div style={{ padding: '10px 14px', background: C.redLight, border: `1px solid ${C.red}44`, borderRadius: '8px', color: C.red, fontSize: '13px', marginBottom: '16px' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Title *</label>
                <input value={form.title} onChange={e => handleFormChange('title', e.target.value)}
                  placeholder="Event title…" style={inputStyle} />
              </div>

              {/* Date row — single column on mobile */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Date *</label>
                  <input type="date" value={form.date} onChange={e => handleFormChange('date', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>End Date</label>
                  <input type="date" value={form.end_date} onChange={e => handleFormChange('end_date', e.target.value)} style={inputStyle} />
                </div>
              </div>

              {/* Venue + Type row — single column on mobile */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Venue</label>
                  <select value={form.venue} onChange={e => handleFormChange('venue', e.target.value)} style={inputStyle}>
                    <option value="All Venues">All Venues</option>
                    {VENUES.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Type</label>
                  <select value={form.type} onChange={e => handleFormChange('type', e.target.value)} style={inputStyle}>
                    {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Colour preview */}
              {(() => {
                const previewColor = resolveColor(form.type, form.venue);
                const previewLight = resolveLight(form.type, form.venue);
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: previewLight, borderRadius: '8px', border: `1px solid ${previewColor}30` }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: previewColor, flexShrink: 0 }} />
                    <span style={{ fontSize: '12px', fontWeight: 600, color: previewColor }}>
                      {EVENT_TYPES.find(t => t.value === form.type)?.label} · {form.venue || 'All Venues'}
                    </span>
                  </div>
                );
              })()}

              <div>
                <label style={labelStyle}>Recurrence</label>
                <select value={form.recurrence} onChange={e => handleFormChange('recurrence', e.target.value)} style={inputStyle}>
                  <option value="none">One-time</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              <div>
                <label style={labelStyle}>Notes <span style={{ color: C.border, fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                <textarea
                  value={form.notes}
                  onChange={e => handleFormChange('notes', e.target.value)}
                  placeholder="Add any relevant details, instructions or context…"
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                />
              </div>

              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '14px' }}>
                  Visibility &amp; Notifications
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <MultiToggle
                    label="Visible to roles"
                    options={ALL_ROLES}
                    selected={form.visible_to_users.length > 0 ? [] : form.visible_to_roles}
                    onChange={val => handleFormChange('visible_to_roles', val)}
                  />
                  {form.visible_to_roles.length < ALL_ROLES.length && !form.restrict_to_users && (
                    <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: C.mid }}>
                      <Lock size={10} color={C.mid} />
                      Hidden from: {ALL_ROLES.filter(r => !form.visible_to_roles.includes(r)).join(', ')}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: form.restrict_to_users ? '#F3EEFF' : C.cream, borderRadius: '10px', border: `1px solid ${form.restrict_to_users ? '#7C3AED55' : C.border}`, marginBottom: form.restrict_to_users ? '12px' : '0', cursor: 'pointer' }}
                    onClick={() => handleFormChange('restrict_to_users', !form.restrict_to_users)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Lock size={14} color={form.restrict_to_users ? '#7C3AED' : C.mid} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: form.restrict_to_users ? '#7C3AED' : C.mid }}>Restrict to specific people</span>
                    </div>
                    <div style={{ width: '36px', height: '20px', borderRadius: '10px', background: form.restrict_to_users ? '#7C3AED' : C.border, position: 'relative', transition: 'background 0.2s' }}>
                      <div style={{ width: '14px', height: '14px', borderRadius: '50%', background: C.white, position: 'absolute', top: '3px', left: form.restrict_to_users ? '19px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                    </div>
                  </div>
                  {form.restrict_to_users && (
                    <div style={{ padding: '12px 14px', background: '#F3EEFF', borderRadius: '10px', border: '1px solid #7C3AED33' }}>
                      <label style={{ ...labelStyle, color: '#7C3AED', marginBottom: '8px' }}>
                        Visible to these people only
                        <span style={{ color: C.mid, fontWeight: 400, textTransform: 'none', marginLeft: '0.3rem' }}>(admins always see all events)</span>
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {adminUsers.map(u => {
                          const selected = form.visible_to_users.includes(u.id);
                          return (
                            <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '6px 8px', borderRadius: '6px', background: selected ? '#EDE9FF' : 'transparent', transition: 'background 0.12s' }}>
                              <input type="checkbox" checked={selected}
                                onChange={() => {
                                  const next = selected
                                    ? form.visible_to_users.filter(id => id !== u.id)
                                    : [...form.visible_to_users, u.id];
                                  handleFormChange('visible_to_users', next);
                                }}
                                style={{ accentColor: '#7C3AED', width: '15px', height: '15px', cursor: 'pointer' }}
                              />
                              <span style={{ fontSize: '13px', fontWeight: selected ? 700 : 500, color: selected ? '#7C3AED' : C.charcoal }}>{u.name}</span>
                              <span style={{ fontSize: '11px', color: C.mid, marginLeft: 'auto' }}>{u.role}</span>
                            </label>
                          );
                        })}
                        {adminUsers.length === 0 && <p style={{ fontSize: '12px', color: C.mid, margin: 0 }}>No admin/manager users found.</p>}
                      </div>
                      {form.visible_to_users.length === 0 && (
                        <p style={{ fontSize: '11px', color: '#7C3AED', fontWeight: 600, marginTop: '8px', marginBottom: 0 }}>
                          ⚠ Select at least one person, or turn off restriction
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: form.notify_enabled ? C.amberLight : C.cream, borderRadius: '10px', border: `1px solid ${form.notify_enabled ? C.amber + '55' : C.border}`, marginBottom: form.notify_enabled ? '14px' : '0', cursor: 'pointer' }}
                  onClick={() => handleFormChange('notify_enabled', !form.notify_enabled)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {form.notify_enabled ? <Bell size={14} color={C.amber} /> : <BellOff size={14} color={C.mid} />}
                    <span style={{ fontSize: '13px', fontWeight: 600, color: form.notify_enabled ? C.amber : C.mid }}>Send notification reminder</span>
                  </div>
                  <div style={{ width: '36px', height: '20px', borderRadius: '10px', background: form.notify_enabled ? C.amber : C.border, position: 'relative', transition: 'background 0.2s' }}>
                    <div style={{ width: '14px', height: '14px', borderRadius: '50%', background: C.white, position: 'absolute', top: '3px', left: form.notify_enabled ? '19px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </div>
                </div>

                {form.notify_enabled && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: '14px', background: C.amberLight, borderRadius: '10px', border: `1px solid ${C.amber}33` }}>
                    <MultiToggle label="Notify roles" options={ALL_ROLES} selected={form.notify_roles} onChange={val => handleFormChange('notify_roles', val)} />
                    <MultiToggle label="Notify venues" options={VENUES} selected={form.notify_venues} onChange={val => handleFormChange('notify_venues', val)} />
                    <div>
                      <label style={labelStyle}>Alert lead time</label>
                      <select value={form.lead_days} onChange={e => handleFormChange('lead_days', parseInt(e.target.value))} style={inputStyle}>
                        <option value={0}>Day of event</option>
                        <option value={1}>1 day before</option>
                        <option value={2}>2 days before</option>
                        <option value={3}>3 days before</option>
                        <option value={7}>1 week before</option>
                        <option value={14}>2 weeks before</option>
                      </select>
                    </div>
                    {form.notify_roles.length === 0 && (
                      <div style={{ fontSize: '11px', color: C.amber, fontWeight: 600 }}>⚠ Select at least one role to notify</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px', borderTop: `1px solid ${C.border}`, paddingTop: '18px' }}>
              <button onClick={closeModal}
                style={{ padding: '0.6rem 1.25rem', border: `1.5px solid ${C.border}`, borderRadius: '8px', background: C.white, cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem', color: C.mid }}>
                Cancel
              </button>
              <button onClick={saveEvent} disabled={saving}
                style={{ padding: '0.6rem 1.75rem', background: saving ? C.border : C.navy, color: saving ? C.mid : C.white, border: 'none', borderRadius: '8px', cursor: saving ? 'default' : 'pointer', fontWeight: 800, fontSize: '0.88rem', transition: 'all 0.15s' }}>
                {saving ? 'Saving…' : editingEvent ? 'Save Changes' : 'Create Event'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Desktop day popup (extracted to keep main component clean) ───────────────
function DesktopDayPopup({ day, dateStr, events, canEdit, isAdmin, onEdit, onDelete, onAdd, onClose, currentMonth, currentYear }) {
  const DAY_NAMES_FULL = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const d = new Date(dateStr);
  const dayName = DAY_NAMES_FULL[d.getDay() === 0 ? 6 : d.getDay() - 1];
  const todayStr = new Date().toISOString().split('T')[0];
  const isToday = dateStr === todayStr;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(47,58,66,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ background: '#fff', borderRadius: '16px', padding: '24px', minWidth: '360px', maxWidth: '500px', width: '90%', boxShadow: '0 12px 40px rgba(0,0,0,0.18)' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#7A8A96', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>{dayName}</div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#2F3A42' }}>
              {day} {MONTH_NAMES[currentMonth]} {currentYear}
              {isToday && <span style={{ marginLeft: '8px', fontSize: '12px', fontWeight: 600, color: '#445366', background: '#FEFCE8', padding: '2px 8px', borderRadius: '10px', border: '1px solid #E4AC49' }}>Today</span>}
            </h3>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {canEdit && (
              <button onClick={onAdd} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', background: '#445366', color: '#fff', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}>
                <Plus size={12} /> Add
              </button>
            )}
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7A8A96', padding: '4px' }}>
              <X size={18} />
            </button>
          </div>
        </div>

        {events.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '28px 0', color: '#7A8A96', fontSize: '13px' }}>
            <div style={{ fontSize: '28px', marginBottom: '8px' }}>📅</div>
            No events scheduled
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {events.map((ev, i) => (
              <div key={ev.id + i} style={{ borderBottom: i < events.length - 1 ? '1px solid #D8DEE3' : 'none' }}>
                <EventListItem ev={ev} canEdit={canEdit} isAdmin={isAdmin} onEdit={onEdit} onDelete={onDelete} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}